import { isUndefined } from "../nullable";
import * as t from "io-ts";
import { chain } from "fp-ts/lib/Either";
import { flow, identity, pipe } from "fp-ts/lib/function";
import { formatUnits } from "@ethersproject/units";
import { either } from "fp-ts";
import {
  BigIntWithDecimals,
  PERCENT_DECIMALS,
  USD_DECIMALS,
} from "@silo/types";
import { BigIntWithDecimalsFromString } from "../bigint-with-decimals";
import { shiftDecimal } from "../bigint";

interface Page {
  first: number;
  skip: number;
}

export const generatePages =
  (pageSize: number, maxSkip?: number) =>
  (total: number): Page[] => {
    if (pageSize <= 0) {
      return [];
    }

    const countOfPages = Math.ceil(total / pageSize);

    const pages = [...Array(countOfPages)].map((_, i) => ({
      first: pageSize,
      skip: i * pageSize,
    }));

    if (isUndefined(maxSkip)) return pages;

    return pages.filter(({ skip }) => skip <= maxSkip);
  };

const GRAPH_PAGE_SIZE = 1000;
const GRAPH_MAX_SKIP = 5000;

export const generateGraphPages = generatePages(
  GRAPH_PAGE_SIZE,
  GRAPH_MAX_SKIP
);

export const USDFromString = BigIntWithDecimalsFromString(USD_DECIMALS);

export interface PercentageFromPointsString
  extends t.Type<BigIntWithDecimals, string, unknown> {}

/**
 * Parses a percentage value denominated in points to a `BigIntWithDecimals`
 *
 * ```ts
 * "2.5" // => { decimals: 18, value: 25_000_000_000_000_000 } (i.e. 0.025 or 2.5%)
 * ```
 */
export const PercentageFromPointsString = new t.Type<
  BigIntWithDecimals,
  string,
  unknown
>(
  "PercentageFromPointsString",
  BigIntWithDecimals.is,
  (u, c) =>
    pipe(
      BigIntWithDecimalsFromString(PERCENT_DECIMALS).validate(u, c),
      chain(({ value, decimals }) => {
        /**
         * The graph sends percentages as points, so need to divide by 100 to get a decimal percentage
         */
        try {
          return t.success({
            value: shiftDecimal(value, -2),
            decimals,
          });
        } catch (error) {
          return t.failure(u, c);
        }
      })
    ),
  ({ value, decimals }) => formatUnits(shiftDecimal(value, 2), decimals)
);

export const getPercentagePointsFromString = flow(
  PercentageFromPointsString.decode,
  either.fold(() => {
    throw new Error("invalid percentage points");
  }, identity)
);
